import { Topic } from 'common/client-data-types/topic'
import { layout } from 'thewest/App.routing'
import { metrics } from 'component-library/__styling/settings'

import { getTopicSponsor } from 'thewest/routes/topic/get-topic-sponsor'
import { BaseClientConfig } from 'common/client/BaseClientConfig'
import { PageType, createPageDefinition, GetRouteAdTargeting } from 'common/routing/page-definition'
import { topicParallax } from './topic-parallax'
import { billboardLeaderboardMrec } from '../ad-units'
import { AdTargeting } from 'common/advertising/add-ads'
import { TheWestSection } from 'component-library/__styling/settings/sections/thewest'
import { renderForPrimaryTopic } from 'common/routing/helpers'

export interface Props {
    topic: Topic
    publicationKind?: 'article' | 'gallery'
    topicSource?: string
    config: BaseClientConfig
    section: TheWestSection
    getAdTargeting: GetRouteAdTargeting
}

export const getTopicPageRouteInfo = ({
    topic,
    publicationKind,
    topicSource,
    section,
    getAdTargeting,
}: Props): PageType<typeof layout, TheWestSection> => {
    const { adUnitPath, ssAdUnits, topics } = getAdTargeting('home', section, topic)

    const adTargeting: AdTargeting = {
        pageId: topic.title,
        adUnitPath,
        ssAdUnits,
        topics,
    }

    const sponsor = getTopicSponsor(topic)
    const isSponsoredCollection = !!sponsor

    const shouldDisplayBreakingNews = () => section === 'default'

    return createPageDefinition<typeof layout, TheWestSection>(
        {
            heading: topic.title,
            hideHeading: true,
            pageType: 'topic',
            includeFromSectionInMetaDescription: !topic.seoDescription,
            pageMeta: {
                description: topic.seoDescription || `The Latest in ${topic.seoTitle}`,
            },
            adTargeting,
            section,
        },
        layout.composition({
            type: 'box',
            props: {
                containerWidth: metrics.thewest.mainContentWidth,
                horizontalGutters: 'outerMargin',
                verticalGutters: ['outerMargin', 'unset'],
            },
            contentAreas: {
                main: [
                    ...(shouldDisplayBreakingNews()
                        ? [
                              layout.component({
                                  type: 'breaking-news',
                                  props: {
                                      dataDefinitionArgs: {
                                          type: 'curation',
                                          name: 'breaking-news',
                                          offset: 0,
                                          pageSize: 1,
                                      },
                                  },
                              }),
                          ]
                        : []),
                    layout.component({
                        type: 'breadcrumb',
                        props: {
                            topic,
                            sponsor,
                            enableHeaderTag: true,
                        },
                    }),
                ],
            },
        }),
        layout.composition({
            type: 'thor',
            props: {
                containerWidth: metrics.thewest.mainContentWidth,
                horizontalGutters: true,
            },
            contentAreas: {
                main: [
                    // Only show cricket widget on non-regional pages
                    ...(section === 'default'
                        ? [
                              ...renderForPrimaryTopic(
                                  topic,
                                  {
                                      allowedTopics: [
                                          'sport',
                                          'sport/cricket',
                                          'sport/australian-cricket-team',
                                          'sport/big-bash-league',
                                          'sport/womens-cricket',
                                          'sport/perth-scorchers',
                                      ],
                                  },
                                  [
                                      layout.component({
                                          type: 'HorizontalCricketWidget',
                                          feature: 'cricket-widgets',
                                          props: {
                                              verticalGutters: ['xs', 'md'],
                                          },
                                      }),
                                  ]
                              ),
                          ]
                        : []),
                    {
                        type: 'lima',
                        props: {
                            verticalSpacing: 'md',
                            fontScale: 1.2,
                            fixedRatios: ['16:9'],
                            initialColumns: 1,
                            intermediateColumns: 2,
                            finalColumns: 4,
                            hasBackground: true,
                            isSponsoredCollection,

                            dataDefinitionArgs: {
                                type: 'listing',
                                topics: [topic.id],
                                includeSubTopics: true,
                                publicationKind,
                                source: topicSource,
                                paging: {
                                    page: 1,
                                    pageSize: 8,
                                },
                            },
                        },
                    },
                    ...topicParallax(),
                    {
                        type: 'lima',
                        props: {
                            verticalSpacing: 'md',
                            fontScale: 1.2,
                            fixedRatios: ['16:9'],
                            hasBackground: true,
                            isSponsoredCollection,
                            initialColumns: 1,
                            intermediateColumns: 2,
                            finalColumns: 4,

                            dataDefinitionArgs: {
                                type: 'listing',
                                topics: [topic.id],
                                includeSubTopics: true,
                                publicationKind,
                                source: topicSource,
                                paging: {
                                    page: 1,
                                    pageOffset: 8,
                                    pageSize: 8,
                                },
                            },
                        },
                    },
                    ...billboardLeaderboardMrec('one'),
                    {
                        type: 'lima',
                        props: {
                            verticalSpacing: 'md',
                            fontScale: 1.2,
                            fixedRatios: ['16:9'],
                            hasBackground: true,
                            isSponsoredCollection,
                            initialColumns: 1,
                            intermediateColumns: 2,
                            finalColumns: 4,

                            loadMoreEnabled: true,

                            dataDefinitionArgs: {
                                type: 'listing',
                                topics: [topic.id],
                                includeSubTopics: true,
                                publicationKind,
                                source: topicSource,
                                paging: {
                                    page: 1,
                                    pageOffset: 16,
                                    pageSize: 8,
                                },
                            },
                        },
                    },
                    ...billboardLeaderboardMrec('two'),
                ],
            },
        })
    )
}
